import type { InvivoAnatomyNode, InvivoSceneDefinition } from "../types/invivo_scene";

interface InvivoSceneRenderScale {
  x: number;
  y: number;
  z: number;
}

interface InvivoSceneRenderVector3 {
  x: number;
  y: number;
  z: number;
}

interface InvivoSceneMeshTransformPlan {
  scale?: InvivoSceneRenderVector3;
  rotation?: InvivoSceneRenderVector3;
  translation?: InvivoSceneRenderVector3;
  uvScale?: {
    u: number;
    v: number;
  };
}

export interface InvivoSceneAnatomyNodePlan {
  id: string;
  label: string;
  meshes: string[];
  ownMeshes: string[];
  visible: boolean;
  variant: boolean;
  defaultVariant: boolean;
  complication: boolean | string | null;
  children: InvivoSceneAnatomyNodePlan[];
}

export interface InvivoSceneRenderPlan {
  meshMaterialAssignments: Record<string, string>;
  rootTransform: {
    scale: InvivoSceneRenderScale;
    rotation: InvivoSceneRenderVector3;
  };
  meshTransforms: Record<string, InvivoSceneMeshTransformPlan>;
  groups: {
    sheet: string[];
    skin: string[];
  };
  anatomyTree: InvivoSceneAnatomyNodePlan[];
  anatomyGroups: InvivoSceneAnatomyNodePlan[];
}

function buildVector3(value: number): InvivoSceneRenderVector3 {
  return {
    x: value,
    y: value,
    z: value,
  };
}

function buildUvVector(value: number): { u: number; v: number } {
  return {
    u: value,
    v: value,
  };
}

function parseAxisVector(parts: string[], initialValue: number): InvivoSceneRenderVector3 {
  const vector = buildVector3(initialValue);
  let axis: keyof InvivoSceneRenderVector3 | null = null;

  parts.forEach((part) => {
    if (part === "x" || part === "y" || part === "z") {
      axis = part;
      return;
    }

    const value = Number(part);

    if (!Number.isFinite(value)) {
      return;
    }

    if (axis) {
      vector[axis] = value;
      axis = null;
      return;
    }

    vector.x = value;
    vector.y = value;
    vector.z = value;
  });

  return vector;
}

function parseUvVector(parts: string[], initialValue: number): { u: number; v: number } {
  const vector = buildUvVector(initialValue);
  let axis: "u" | "v" | null = null;

  parts.forEach((part) => {
    if (part === "u" || part === "v") {
      axis = part;
      return;
    }

    const value = Number(part);

    if (!Number.isFinite(value)) {
      return;
    }

    if (axis) {
      vector[axis] = value;
      axis = null;
      return;
    }

    vector.u = value;
    vector.v = value;
  });

  return vector;
}

function ensureMeshTransform(
  meshTransforms: Record<string, InvivoSceneMeshTransformPlan>,
  meshName: string
): InvivoSceneMeshTransformPlan {
  meshTransforms[meshName] ||= {};

  return meshTransforms[meshName];
}

function asArray<T>(value: T | T[] | undefined): T[] {
  if (value === undefined) {
    return [];
  }

  return Array.isArray(value) ? value : [value];
}

function mergeMeshMaterialAssignments(
  assignments: Record<string, string>,
  meshes: string | string[] | undefined,
  material: string | undefined
): void {
  if (!meshes || !material) {
    return;
  }

  asArray(meshes).forEach((meshName) => {
    assignments[meshName] = material;
  });
}

function slugifyAnatomyGroupId(caption: string, fallback: string): string {
  const slug = caption
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

  return slug || fallback;
}

function uniqueNodeId(baseId: string, usedIds: Map<string, number>): string {
  const count = usedIds.get(baseId) || 0;
  usedIds.set(baseId, count + 1);

  return count === 0 ? baseId : `${baseId}-${count + 1}`;
}

function normalizeAnatomyNodes({
  nodes,
  inheritedMaterial,
  inheritedVisible,
  assignments,
  usedIds,
  path,
}: {
  nodes: InvivoAnatomyNode[];
  inheritedMaterial?: string;
  inheritedVisible: boolean;
  assignments: Record<string, string>;
  usedIds: Map<string, number>;
  path: string;
}): InvivoSceneAnatomyNodePlan[] {
  let selectedVariantIndex = -1;

  nodes.forEach((node, index) => {
    if (node.variant && node.visible === true) {
      selectedVariantIndex = index;
    }
  });

  return nodes.map((node, index) => {
    const label = node.caption || `Group ${index + 1}`;
    const variant = node.variant === true;
    const defaultVariant = variant && (selectedVariantIndex === index || selectedVariantIndex < 0);
    const visible = variant ? defaultVariant : "visible" in node ? node.visible !== false : inheritedVisible;
    const material = typeof node.material === "string" ? node.material : inheritedMaterial;
    const ownMeshes = asArray(node.meshes);
    const id = uniqueNodeId(slugifyAnatomyGroupId(label, `${path}-${index + 1}`), usedIds);

    mergeMeshMaterialAssignments(assignments, ownMeshes, material);

    const children = normalizeAnatomyNodes({
      nodes: node.children || [],
      inheritedMaterial: material,
      inheritedVisible: visible,
      assignments,
      usedIds,
      path: id,
    });
    const meshes = [...ownMeshes, ...children.flatMap((child) => child.meshes)];

    return {
      id,
      label,
      meshes,
      ownMeshes,
      visible,
      variant,
      defaultVariant,
      complication: node.complication || null,
      children,
    };
  });
}

function applyGroupMaterials(definition: InvivoSceneDefinition, assignments: Record<string, string>): void {
  Object.entries(definition.groups || {}).forEach(([groupName, meshNames]) => {
    const materialName = groupName === "sheet" ? "Sheet" : groupName === "skin" ? "Skin" : null;

    if (!materialName) {
      return;
    }

    meshNames.forEach((meshName) => {
      assignments[meshName] ||= materialName;
    });
  });
}

function applySceneSetup(
  definition: InvivoSceneDefinition,
  assignments: Record<string, string>
): Pick<InvivoSceneRenderPlan, "rootTransform" | "meshTransforms"> {
  const rootTransform = {
    scale: buildVector3(1),
    rotation: buildVector3(0),
  };
  const meshTransforms: Record<string, InvivoSceneMeshTransformPlan> = {};

  (definition.scene_setup || []).forEach((command) => {
    const parts = String(command).trim().split(/\s+/);
    const head = parts.shift();

    if (!head) {
      return;
    }

    const separatorIndex = head.indexOf(".");
    const targetMeshName = separatorIndex >= 0 ? head.slice(0, separatorIndex) : null;
    const operation = separatorIndex >= 0 ? head.slice(separatorIndex + 1) : head;

    if (operation === "material") {
      const meshName = targetMeshName;
      const materialName = parts[0];

      if (meshName && materialName) {
        assignments[meshName] = materialName;
      }

      return;
    }

    if (operation === "scale") {
      const hasUvAxis = parts.includes("u") || parts.includes("v");

      if (hasUvAxis) {
        if (targetMeshName) {
          ensureMeshTransform(meshTransforms, targetMeshName).uvScale = parseUvVector(parts, 1);
        }
        return;
      }

      const scale = parseAxisVector(parts, 1);

      if (targetMeshName) {
        ensureMeshTransform(meshTransforms, targetMeshName).scale = scale;
      } else {
        rootTransform.scale = scale;
      }

      return;
    }

    if (operation === "translate") {
      const translation = parseAxisVector(parts, 0);

      if (targetMeshName) {
        ensureMeshTransform(meshTransforms, targetMeshName).translation = translation;
      }

      return;
    }

    if (operation === "rotate" || operation === "yaw" || operation === "pitch" || operation === "roll") {
      const rotation =
        operation === "rotate"
          ? parseAxisVector(parts, 0)
          : {
              x: operation === "pitch" ? Number(parts[0]) || 0 : 0,
              y: operation === "yaw" ? Number(parts[0]) || 0 : 0,
              z: operation === "roll" ? Number(parts[0]) || 0 : 0,
            };

      if (targetMeshName) {
        ensureMeshTransform(meshTransforms, targetMeshName).rotation = rotation;
      } else {
        rootTransform.rotation = rotation;
      }
    }
  });

  return {
    rootTransform,
    meshTransforms,
  };
}

export function buildSceneRenderPlan(definition: InvivoSceneDefinition): InvivoSceneRenderPlan {
  const meshMaterialAssignments: Record<string, string> = {};
  const anatomyTree = normalizeAnatomyNodes({
    nodes: definition.anatomy,
    inheritedMaterial: undefined,
    inheritedVisible: true,
    assignments: meshMaterialAssignments,
    usedIds: new Map(),
    path: "root",
  });

  applyGroupMaterials(definition, meshMaterialAssignments);

  const sceneSetup = applySceneSetup(definition, meshMaterialAssignments);

  return {
    meshMaterialAssignments,
    rootTransform: sceneSetup.rootTransform,
    meshTransforms: sceneSetup.meshTransforms,
    groups: {
      sheet: definition.groups?.sheet || [],
      skin: definition.groups?.skin || [],
    },
    anatomyTree,
    anatomyGroups: anatomyTree,
  };
}
